import * as React from 'react';
// Import props.
import {
	ComparativeDataHeaderProps,
	ComparativeDataRowProps,
	ComparativePolicyCostSummaryProps,
	IconButtonProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
} from '@app/enumerations';
// Import api models.
import {
	PolicyCost,
	PolicyCostLineItem
} from '@app/models/api';
// Import services.
import {
	DateService,
	StringService,
} from '@app/services';
// Import material ui modules.
import {
	CreditCardOutlined,
	MonetizationOnOutlined,
} from '@mui/icons-material';

interface UseComparativePolicyCostSummaryParameters {
	props: ComparativePolicyCostSummaryProps;
}

interface UseComparativePolicyCostSummaryResults {
	applyPolicyCostComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyPolicyCostCurrentTermComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyPolicyCostNextTermComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyPolicyCostCurrentTermComparativeDataRowProps: () => ComparativeDataRowProps[];
	applyPolicyCostNextTermComparativeDataRowProps: () => ComparativeDataRowProps[];
	policyCostBaseId: string;
	policyCostCurrentTermBaseId: string;
	policyCostNextTermBaseId: string;
	showCurrentTermCost: boolean;
	showNextTermCost: boolean;
}

const useComparativePolicyCostSummary = (params: UseComparativePolicyCostSummaryParameters): UseComparativePolicyCostSummaryResults => {

	// #region exported methods

	/**
	 * Returns policy cost comparative data header props. 
	 **/
	const applyPolicyCostComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${policyCostBaseId}-header`,
			isSticky: areHeadersSticky,
			title: 'Policy Cost',
			titleFontSize: 'md',
			titleIcon: MonetizationOnOutlined,
			paddingProps: {
				bottom: 1,
				top: 1,
			},
		};
	};

	/**
	 * Returns policy cost current term comparative data header props. 
	 **/
	const applyPolicyCostCurrentTermComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${policyCostCurrentTermBaseId}-header`,
			isSticky: false,
			title: 'Current Term Annual Cost',
			titleFontSize: 'sm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns policy cost next term comparative data header props. 
	 **/
	const applyPolicyCostNextTermComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		const titleColor: AppThemeColor = params.props.isPublicPolicyPaymentDue ? AppThemeColor.ERROR : AppThemeColor.INFO;
		return {
			id: `${policyCostNextTermBaseId}-header`,
			isSticky: false,
			title: nextTermCostTitleLabel,
			titleFontSize: 'sm',
			titleColor: titleColor,
			iconButtons: getHeaderIconButtonsForNextTermPolicyCostHeader(),
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns policy cost current term comparative data row props. 
	 **/
	const applyPolicyCostCurrentTermComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return applyPolicyCostComparativeDataRowProps(params.props.currentTermPolicyCost ?? undefined);
	}

	/**
	 * Returns policy cost next term comparative data row props. 
	 **/
	const applyPolicyCostNextTermComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return applyPolicyCostComparativeDataRowProps(params.props.nextTermPolicyCost ?? undefined, nextTermDateRangeStr);
	}

	// #endregion exported methods


	// #region private methods

	/**
 * Returns policy cost comparative data row props. 
 **/
	const applyPolicyCostComparativeDataRowProps = (policyCost: PolicyCost | undefined, termDateRangeStr: string | undefined = undefined): ComparativeDataRowProps[] => {
		const currentTermPolicyCostLineItems: PolicyCostLineItem[] = policyCost?.lineItems ?? [];
		let dataRowProps: ComparativeDataRowProps[] = [];

		if (termDateRangeStr !== undefined) {
			dataRowProps.push({
				fieldLabel: 'Renewal Term',
				oldValue: termDateRangeStr,
				hideNewValueIfSame: true,
			});
		}

		const dataRowCostProps: ComparativeDataRowProps[] = currentTermPolicyCostLineItems
			.sort((lineItem1, lineItem2) => {
				return (lineItem1.lineItemNumber ?? 0) - (lineItem2.lineItemNumber ?? 0);
			})
			.filter((lineItem) => {
				return lineItem.amount !== null && lineItem.amount !== 0;
			})
			.map((lineItem) => {
				return {
					fieldLabel: lineItem.name ?? String.empty(),
					oldValue: StringService.getInstance().toCurrencyString(lineItem.amount ?? 0),
					hideNewValueIfSame: true,
				}
			});

		dataRowProps = dataRowProps.concat([...dataRowCostProps]);

		dataRowProps.push({
			fieldLabel: 'Total',
			oldValue: StringService.getInstance().toCurrencyString(policyCost?.totalCost ?? 0),
			hideNewValueIfSame: true,
		});

		return dataRowProps;
	}

	/**
	 * Fetches the header icons.
	 */
	const getHeaderIconButtonsForNextTermPolicyCostHeader = (): IconButtonProps[] => {
		let iconButtons: IconButtonProps[] = [];

		if (params.props.isPublicPolicyPaymentDue
			&& params.props.onMakePayment !== undefined) {
			iconButtons.push({
				label: 'Make Payment',
				onClick: params.props.onMakePayment,
				icon: CreditCardOutlined,
			});
		}
		return iconButtons;
	}

	// #endregion private methods


	// #region variables

	const nextTermDateRangeEffectiveDateStr: string | undefined = React.useMemo(() => {
		let newNextTermDateRangeEffectiveDateStr: string | undefined = undefined;
		if (params.props.nextTermEffectiveDate !== undefined) {
			newNextTermDateRangeEffectiveDateStr = DateService.getInstance().formatDate(params.props.nextTermEffectiveDate);
		}
		return newNextTermDateRangeEffectiveDateStr;
	}, [params.props.nextTermEffectiveDate]);

	const nextTermDateRangeEndDateStr: string | undefined = React.useMemo(() => {
		let newNextTermDateRangeEndDateStr: string | undefined = undefined;
		if (params.props.nextTermEndDate !== undefined) {
			newNextTermDateRangeEndDateStr = DateService.getInstance().formatDate(params.props.nextTermEndDate);
		}
		return newNextTermDateRangeEndDateStr;
	}, [params.props.nextTermEndDate]);

	const nextTermDateRangeStr: string | undefined = React.useMemo(() => {
		let newNextTermDateRangeStr: string | undefined = undefined;
		if (nextTermDateRangeEffectiveDateStr !== undefined
			&& nextTermDateRangeEndDateStr !== undefined) {
			newNextTermDateRangeStr = `${nextTermDateRangeEffectiveDateStr} - ${nextTermDateRangeEndDateStr}`;
		}
		return newNextTermDateRangeStr;
	}, [nextTermDateRangeEffectiveDateStr, nextTermDateRangeEndDateStr]);

	const nextTermCostTitleLabel: string = React.useMemo(() => {
		let newNextTermCostTitleLabel: string = 'Next Term Annual Cost';
		if (params.props.isPublicPolicyPaymentDue
			&& params.props.nextTermEffectiveDate !== undefined) {
			newNextTermCostTitleLabel = `Renewal Payment Due - ${DateService.getInstance().formatDate(params.props.nextTermEffectiveDate)}`
		}
		return newNextTermCostTitleLabel;
	}, [params.props.nextTermEffectiveDate, params.props.isPublicPolicyPaymentDue]);

	const showCurrentTermCost: boolean = React.useMemo(() => {
		return params.props.currentTermPolicyCost !== undefined;
	}, [params.props.currentTermPolicyCost])

	const showNextTermCost: boolean = React.useMemo(() => {
		return params.props.nextTermPolicyCost !== undefined;
	}, [params.props.nextTermPolicyCost])

	const policyCostBaseId: string = React.useMemo(() => {
		return `${params.props.id}-policyCost`;
	}, [params.props.id]);

	const policyCostCurrentTermBaseId: string = React.useMemo(() => {
		return `${policyCostBaseId}-currentTerm`;
	}, [policyCostBaseId]);

	const policyCostNextTermBaseId: string = React.useMemo(() => {
		return `${policyCostBaseId}-nextTerm`;
	}, [policyCostBaseId]);

	const areHeadersSticky: boolean = React.useMemo(() => {
		return params.props.areHeadersSticky ?? false;
	}, [params.props.areHeadersSticky]);

	// #endregion private variables

	// #region lifecycle methods.

	// #endregion lifecycle methods.

	return {
		applyPolicyCostComparativeDataHeaderProps,
		applyPolicyCostCurrentTermComparativeDataHeaderProps,
		applyPolicyCostNextTermComparativeDataHeaderProps,
		applyPolicyCostCurrentTermComparativeDataRowProps,
		applyPolicyCostNextTermComparativeDataRowProps,
		showCurrentTermCost,
		showNextTermCost,
		policyCostBaseId,
		policyCostCurrentTermBaseId,
		policyCostNextTermBaseId,
	};
}

// Export parameters.
export {
	UseComparativePolicyCostSummaryParameters,
}

// Export results.
export {
	UseComparativePolicyCostSummaryResults,
}

export default useComparativePolicyCostSummary;